/**
 * Access shell — sign-in, reset, verify and onboarding screens (Reppr Elite).
 *
 * Layout per the handoff: a fixed 440px left panel on --surface-panel carrying
 * the lockup and proof content; the form fills the right. The mock's top tab
 * strip is a review affordance and is deliberately not shipped.
 *
 * The mock's aside carried a fictional testimonial, invented aggregate stats
 * ("$4.1B under representation", "1,240 athletes") and a SOC 2 badge. None of
 * those are true of this product today, so none are shipped. The aside instead
 * states real capabilities — things the app verifiably does — and the security
 * line lists only what actually exists.
 */

import { type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import styles from './AccessShell.module.css'

/**
 * What the rail says to someone signing in.
 *
 * It used to carry four pieces of product mechanics in mono capitals — the
 * nightly market sync, percentile ranking, signed links, recorded reasons.
 * That is shop talk: it explains how the product works to people who are only
 * trying to get to their desk, and it publishes it to anyone who loads the
 * page. These are the plain reasons an agent keeps the tab open.
 */
const PROOF: { key: string; label: string }[] = [
  { key: 'Your book', label: 'Clients, contracts and deals in one place' },
  { key: 'Your money', label: 'What is due, when, and your fee on it' },
  { key: 'Your clients', label: 'A private statement each, on their phone' },
]

interface AccessShellProps {
  eyebrow: string
  title: string
  blurb: string
  /** Max width of the form column, e.g. 440 or 680. */
  width?: number
  children: ReactNode
}

export default function AccessShell({ eyebrow, title, blurb, width = 440, children }: AccessShellProps) {
  return (
    <div className={styles.shell}>
      <aside className={styles.aside}>
        <Link to="/" className={styles.lockup}>
          <img src="/icon-512.png" alt="" width="26" height="26" className={styles.mark} />
          <span className={styles.lockupText}>
            <span className={styles.wordmark}>Reppr</span>
            <span className={styles.lockupSub}>Sports representation</span>
          </span>
        </Link>

        <div className={styles.practice}>
          <span className={styles.practiceEyebrow}>The working desk</span>
          <p className={styles.practiceLine}>
            The whole book on one desk: every client, every contract, and the money
            against each — so you can answer a family before they ask.
          </p>
        </div>

        <div className={styles.proof}>
          <div className={styles.proofGrid}>
            {PROOF.map(p => (
              <div key={p.key}>
                <div className={styles.proofKey}>{p.key}</div>
                <div className={styles.proofLabel}>{p.label}</div>
              </div>
            ))}
          </div>
          <p className={styles.securityLine}>Your data stays yours · Export any time</p>
        </div>
      </aside>

      <main className={styles.main}>
        <div className={styles.formCol} style={{ maxWidth: width }}>
          <div className={styles.head}>
            <span className={styles.eyebrow}>{eyebrow}</span>
            <h1 className={styles.title}>{title}</h1>
            <p className={styles.blurb}>{blurb}</p>
          </div>
          {children}
        </div>
      </main>
    </div>
  )
}

/** Field row per the Access spec: label + optional aide, boxed input, hint. */
export function AccessField({ label, htmlFor, aide, hint, children }: {
  label: string
  htmlFor?: string
  /** Right-aligned aide — a link or state note (e.g. "Forgot password?"). */
  aide?: ReactNode
  hint?: string
  children: ReactNode
}) {
  return (
    <div className={styles.field}>
      <div className={styles.fieldLabelRow}>
        <label className={styles.fieldLabel} htmlFor={htmlFor}>{label}</label>
        {aide && <span className={styles.fieldAide}>{aide}</span>}
      </div>
      <div className={styles.fieldBox}>{children}</div>
      {hint && <div className={styles.fieldHint}>{hint}</div>}
    </div>
  )
}

/** Brass-railed callout banner (reset expiry, verify status, etc.). */
export function AccessBanner({ title, body }: { title: string; body: ReactNode }) {
  return (
    <div className={styles.banner}>
      <span className={styles.bannerRail} aria-hidden="true" />
      <div>
        <div className={styles.bannerTitle}>{title}</div>
        <div className={styles.bannerBody}>{body}</div>
      </div>
    </div>
  )
}

/** CTA row: brass primary, quiet secondary, mono meta. */
export function AccessCtas({ primary, primaryHref, primaryType = 'submit', onPrimary, disabled, secondary, meta }: {
  primary: string
  /** Renders the primary action as a link rather than a button. For
   *  destinations off the app, where a button denies the middle click and
   *  the open-in-new-tab that people expect of a link. */
  primaryHref?: string
  primaryType?: 'submit' | 'button'
  onPrimary?: () => void
  disabled?: boolean
  secondary?: ReactNode
  meta?: string
}) {
  return (
    <div className={styles.ctas}>
      {primaryHref ? (
        <a className={styles.primary} href={primaryHref} target="_blank" rel="noopener noreferrer">
          {primary}
        </a>
      ) : (
        <button type={primaryType} className={styles.primary} onClick={onPrimary} disabled={disabled}>
          {primary}
        </button>
      )}
      {secondary}
      {meta && <span className={styles.ctaMeta}>{meta}</span>}
    </div>
  )
}

/** "No account yet? · Request access" alternate row. */
export function AccessAlt({ prompt, children }: { prompt: string; children: ReactNode }) {
  return (
    <div className={styles.alt}>
      <span className={styles.altPrompt}>{prompt}</span>
      {children}
    </div>
  )
}

export function AccessFoot({ children }: { children: ReactNode }) {
  return <p className={styles.foot}>{children}</p>
}
